import _ from "lodash";
import client from "lib/apolloClient/client";
import {
  getProjectGroupDeadlinesQuery,
  getProjectGroupGuidanceStatusQuery,
  getProjectGroupSelectedReportTemplateQuery,
  getProjectGroupProjectCountQuery,
  getProjectDetailsQuery,
  getProjectStudentCollaboratorsForSplittingQuery,
  getProjectFinalReportQuery,
  getProjectInfoQuery,
  getMultiNodeProjectCountsQuery,
  getGuidingQuestionsQuery,
  getPlannerElementInsightSpecificsQuery,
  getProjectsWithEvidenceInsightsQuery,
  getPostsQuery,
  getOrganizationStudentsQuery,
  getProjectGroupSupervisorsQuery,
  getProjectGroupStudentProjectPortfoliosCountQuery,
  getProjectGroupStudentProjectAdvisersQuery,
  getStudentProjectPortfolioDetailsQuery,
  getStudentPortfolioProjectsQuery,
  getLearningOutcomesWithEvidencesQuery,
  getCASInsightQuery,
  getCASPlannerElementEvidencesQuery,
  getProjectGroupStudentPortfolioIdQuery,
  getUserProjectGroupsQuery,
  getProjectGroupYearGroupQuery,
  getStudentProjectPortfolioQuery,
  getUserProjectGroupYearGroupsQuery,
  getProjectGroupAssessmentToolDetailsQuery,
  getOrganizationAcademicCriteriaValuesQuery,
  getProjectGroupDetailsQuery,
  getProjectCriteriaDetailsQuery,
  getProjectStudentCriteriaDetailsQuery,
  getStudentsBulkDownloadReportsQuery,
  getProjectGroupPortfoliosDataQuery,
  getProjectGroupSetupGuidanceDetailsQuery,
  getStudentTagDetailsQuery,
  getProjectGroupCourseQuery,
  getOrganizationCourseAndOtherStaffQuery,
  getGroup4ProjectStudentsDetailsQuery,
  getProjectGroupSubjectGroupIdQuery,
  getGroup4ProjectSubjectDetailsQuery,
  getYearGroupStudentsQuery,
  getMultiNodeProjectGroupProjectCountQuery,
  getUserProjectGroupPortfoliosCountQuery,
  getStudentPortfolioSupervisorsQuery,
  getProjectHeaderInfoQuery,
  getCourseAllCollaboratorsQuery,
  getProjectGroupCourseAcademicYearsQuery,
  getStudentProjectPortfolioFeedbackQuery,
  getModuleAdminsQuery,
  getProjectGroupProjectAndPortfolioCountQuery,
  getProjectGroupProjectStudentsQuery,
  getProjectGroupProjectSupervisorsQuery,
  getOrganizationStaffWithExternalUsersQuery,
  getProjectGroupBasicDetailsQuery,
  getProjectApprovedSupervisorsQuery,
  getDPSubjectGroupsQuery,
  getOrgAdminsQuery,
  getLearningOutcomesPostsQuery,
  getFinalGradeRatingQuery,
  getStudentProjectPortfolioHeaderDetailsQuery,
  getProjectGroupProjectsIdsQuery,
  getProjectGroupCategoriesQuery,
  getProjectGroupAcademicYearsQuery,
  getOrganizationAcademicCriteriaValuesMultiDPQuery,
} from "./ProjectQueries";
import {
  deadlineFragments,
  projectFragments,
  guidingQuestionsFragment,
  studentPortfolioProgressFragment,
  projectGroupFragment,
  feedbackPostFragment,
  projectFieldsResolvedMinimalTreeFragment,
} from "./ProjectFragments";
import { projectAttachmentFragment } from "UnitPlans/modules/UnitPlanFragments";
import { Sentry } from "services/sentry";
import { plannerElementNodeFragment } from "modules/CommonFragments";
import ACLStore from "lib/aclStore";
import { getProjectHeaderInfoVariables } from "./Utils";

export const getProjectGroupDeadlinesFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupDeadlinesQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getProjectFinalReportFromCache = ({ projectId }) => {
  try {
    const queryData = client.readQuery({
      query: getProjectFinalReportQuery,
      variables: {
        id: projectId,
      },
    });
    return queryData ? queryData : {};
  } catch (e) {
    console.warn(e);
    return {};
  }
};

export const getProjectGroupSelectedReportTemplateFromCache = ({
  projectGroupId,
}) => {
  try {
    const queryData = client.readQuery({
      query: getProjectGroupSelectedReportTemplateQuery,
      variables: {
        projectGroupId,
      },
    });
    return queryData ? queryData : {};
  } catch (e) {
    console.warn("ERROR getProjectGroupSelectedReportTemplateFromCache", e);
    return {};
  }
};

export const writeProjectGroupDeadlineInCache = ({
  projectDeadlineId,
  data,
}) => {
  try {
    client.writeFragment({
      id: `ProjectDeadline:${projectDeadlineId}`,
      fragment: deadlineFragments.deadlineFragmentWithStatusCount,
      fragmentName: "projectDeadline",
      data,
    });
  } catch (e) {
    return {};
  }
};

export const writeProjectProjectFieldValueInCache = ({ fieldId, data }) => {
  try {
    client.writeFragment({
      id: `PlannerField:${fieldId}`,
      fragment:
        projectFieldsResolvedMinimalTreeFragment.fieldsResolvedMinimalTreeFragment,
      fragmentName: "fieldsResolvedMinimalTreeFragmentItem",
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getProjectGroupGuidanceStatusFromCache = ({ id }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupGuidanceStatusQuery,
      variables: {
        id: id,
      },
    });
  } catch (e) {
    console.warn(e);
    return [];
  }
  if (queryData != null) {
    return queryData;
  } else {
    return [];
  }
};

export const writeProjectGuidanceStatusInCache = ({
  projectGroupId,
  visibilityKey,
  isVisible,
}) => {
  let guidanceStatus = getProjectGroupGuidanceStatusFromCache({
    id: projectGroupId,
  });
  guidanceStatus = _.get(guidanceStatus, "node", {});
  client.writeQuery({
    query: getProjectGroupGuidanceStatusQuery,
    variables: {
      id: projectGroupId,
    },
    data: {
      node: { ...guidanceStatus, [visibilityKey]: isVisible },
    },
  });
};

export const getProjectGroupProjectCountFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupProjectCountQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getMultiNodeProjectGroupProjectCountQueryFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getMultiNodeProjectGroupProjectCountQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getStudentTagDetailsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentTagDetailsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getProjectStudentInfoFragmentFromCache = ({
  projectId,
  projectGroupId,
  studentCourseFilters,
  projectGroupProjectFilters,
  skipStudentDetails,
  skipCourseDetails,
}) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `Project:${projectId}`,
      fragment: projectFragments.projectStudentInfoFragment,
      fragmentName: "projectStudentInfo",
      variables: {
        id: projectGroupId,
        studentCourseFilters,
        filters: projectGroupProjectFilters,
        skipStudentDetails,
        skipCourseDetails,
      },
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectInfoProgressFragmentFromCache = ({
  id,
  studentCourseFilters,
  isServiceAsAction,
  academicYearId,
  skipCourseDetails,
}) => {
  let queryData = {};

  try {
    queryData = client.readFragment({
      id: `Project:${id}`,
      fragment: projectFragments.projectInfoProgressFragment,
      fragmentName: "projectInfoProgress",
      variables: {
        id,
        studentCourseFilters,
        isServiceAsAction,
        academicYearId,
        skipCourseDetails,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const writeProjectInfoProgressFragmentInCache = ({
  projectId,
  data,
  academicYearId,
  skipCourseDetails,
  studentCourseFilters,
  isServiceAsAction,
}) => {
  try {
    client.writeFragment({
      id: `Project:${projectId}`,
      fragment: projectFragments.projectInfoProgressFragment,
      fragmentName: "projectInfoProgress",
      data: data,
      variables: {
        academicYearId,
        skipCourseDetails,
        studentCourseFilters,
        isServiceAsAction,
      },
    });
  } catch (e) {
    console.warn("could not write", e);
    return {};
  }
};

export const getStudentsBulkDownloadReportsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentsBulkDownloadReportsQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getProjectGroupPortfoliosDataFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupPortfoliosDataQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const writeProjectGroupProjectCountInCache = ({ id, filters, data }) => {
  try {
    client.writeQuery({
      query: getProjectGroupProjectCountQuery,
      variables: {
        id: id,
        filters,
      },
      data,
    });
  } catch (e) {
    console.warn("Unable to write ", e);
  }
};

export const getProjectDetailsFromCache = ({
  projectId,
  projectGroupType,
  academicYearId,
  isQueryLoading = false,
}) => {
  let queryData = {};
  let skipErrorLog = false;
  let isQuerySuccess = false;

  try {
    queryData = client.readQuery({
      query: getProjectDetailsQuery,
      variables: {
        id: projectId,
        studentProjectPortfolioFilter: {
          projectGroupTypes: [projectGroupType],
        },
        academicYearId,
        completionStatusFilters: {
          academicYearIds: [academicYearId],
        },
      },
    });
    isQuerySuccess = true;
  } catch (e) {
    if (
      _.includes(_.get(e, "message"), "Can't find field") ||
      _.get(e, "networkError")
    ) {
      skipErrorLog = true;
    }

    if (
      !skipErrorLog &&
      !isQueryLoading &&
      ACLStore.can("FeatureFlag:ProjectsTemplatesSentryDebugLog")
    ) {
      Sentry.captureMessage("PROJECT_DETAILS_CACHE_READ_ERROR", {
        extra: {
          functionName: "getProjectDetailsFromCache",
          variables: { projectId, projectGroupType, academicYearId },
          reason: "warningBlock",
          warning: e,
        },
      });
    }
    console.warn(e);
  }

  if (
    !skipErrorLog &&
    !isQueryLoading &&
    ACLStore.can("FeatureFlag:ProjectsTemplatesSentryDebugLog")
  ) {
    if (_.isEmpty(queryData)) {
      Sentry.captureMessage("PROJECT_DETAILS_EMPTY_CACHE_DATA", {
        extra: {
          functionName: "getProjectDetailsFromCache",
          variables: { projectId, projectGroupType, academicYearId },
          reason: "queryDataEmpty",
        },
      });
    } else if (_.isEmpty(_.get(queryData, "node.template"))) {
      Sentry.captureMessage("PROJECT_DETAILS_EMPTY_TEMPLATE_DATA", {
        extra: {
          functionName: "getProjectDetailsFromCache",
          variables: { projectId, projectGroupType, academicYearId },
          reason: "templateEmpty",
        },
      });
    }
  }

  return queryData ? queryData : {};
};

export const getProjectStudentCollaboratorsForSplittingFromCache = params => {
  try {
    const queryData = client.readQuery({
      query: getProjectStudentCollaboratorsForSplittingQuery,
      variables: params,
    });
    return queryData ? queryData : {};
  } catch (e) {
    console.warn(e);
    return {};
  }
};

export const getCourseAllCollaboratorsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCourseAllCollaboratorsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const writeProjectDetailsInCache = ({
  projectId,
  data,
  academicYearId,
  projectGroupType,
}) => {
  try {
    client.writeQuery({
      query: getProjectDetailsQuery,
      variables: {
        id: projectId,
        academicYearId,
        studentProjectPortfolioFilter: {
          projectGroupTypes: [projectGroupType],
        },
        completionStatusFilters: {
          academicYearIds: [academicYearId],
        },
      },
      data,
    });
  } catch (e) {
    console.warn(e);
  }
};

export const getProjectInfoFromCache = ({ projectId }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectInfoQuery,
      variables: {
        projectId,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectHeaderInfoFromCache = ({
  projectId,
  projectGroupType,
  projectGroupCategoryType,
  academicYearId,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectHeaderInfoQuery,
      variables: getProjectHeaderInfoVariables({
        projectId,
        projectGroupType,
        projectGroupCategoryType,
        academicYearId,
      }),
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const writeProjectHeaderInfoInCache = ({
  projectId,
  data,
  projectGroupType,
  projectGroupCategoryType,
  academicYearId,
}) => {
  try {
    client.writeQuery({
      query: getProjectHeaderInfoQuery,
      variables: getProjectHeaderInfoVariables({
        projectId,
        projectGroupType,
        projectGroupCategoryType,
        academicYearId,
      }),
      data,
    });
  } catch (e) {
    console.warn(e);
  }
};

export const writeProjectInfoInCache = ({ projectId, data }) => {
  try {
    client.writeFragment({
      id: `Project:${projectId}`,
      fragment: projectFragments.projectCompletedDeadlinesFragment,
      fragmentName: "projectCompletedDeadlinesItem",
      data: data,
    });
  } catch (e) {
    console.warn("could not write", e);
    return {};
  }
};

export const getMultiNodeProjectCountsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getMultiNodeProjectCountsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getGuidingQuestionsFromCache = ({ projectGroupId, filters }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getGuidingQuestionsQuery,
      variables: { projectGroupId, filters },
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};
export const writeGuidingQuestionsInCache = ({ data, variables }) => {
  try {
    client.writeQuery({
      query: getGuidingQuestionsQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const writeGuidingQuestionFragmentInCache = ({ id, data }) => {
  try {
    client.writeFragment({
      id: `ProjectGroupGuidingQuestion:${id}`,
      fragment: guidingQuestionsFragment.guidingQuestions,
      fragmentName: "guidingQuestions",
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getGuidingQuestionFragmentFromCache = ({ id }) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `ProjectGroupGuidingQuestion:${id}`,
      fragment: guidingQuestionsFragment.guidingQuestions,
      fragmentName: "guidingQuestions",
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};
export const getPlannerElementInsightSpecificsFromCache = variables => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getPlannerElementInsightSpecificsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  return queryData != null ? queryData : {};
};

export const getProjectsFromCache = ({ ids, filters, evidenceFilters }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getProjectsWithEvidenceInsightsQuery,
      variables: { ids, filters, evidenceFilters },
    });
  } catch (e) {
    return {};
  }

  return queryData != null ? queryData : {};
};

export const getPostsFromCache = ({ ids }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPostsQuery,
      variables: { ids },
    });
  } catch (e) {
    return {};
  }

  return queryData != null ? queryData : {};
};

export const getProjectGroupSupervisorsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupSupervisorsQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectGroupStudentProjectPortfoliosCountFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupStudentProjectPortfoliosCountQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectGroupStudentProjectAdvisersFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupStudentProjectAdvisersQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getStudentProjectPortfolioDetailsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentProjectPortfolioDetailsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const writeStudentProjectPortfolioDetailsInCache = ({ id, data }) => {
  try {
    client.writeQuery({
      query: getStudentProjectPortfolioDetailsQuery,
      variables: {
        id,
      },
      data,
    });
  } catch (e) {
    console.warn("Unable to write : ", e);
  }
};

export const readFeedbackPostFragmentFromCache = params => {
  let queryData = {};

  try {
    queryData = client.readFragment({
      id: `Post:${params.id}`,
      fragment: feedbackPostFragment.feedbackPost,
      fragmentName: "feedbackPostItem",
    });
  } catch (e) {
    console.warn(e);
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const writeFeedbackPostFragmentInCache = ({ id, data }) => {
  try {
    client.writeFragment({
      id: `Post:${id}`,
      fragment: feedbackPostFragment.feedbackPost,
      fragmentName: "feedbackPostItem",
      data,
    });
  } catch (e) {
    console.error(e);
  }
};

export const getStudentPortfolioProjectsFromCache = params => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getStudentPortfolioProjectsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getProjectGroupProjectSupervisorsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupProjectSupervisorsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getProjectGroupProjectStudentsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupProjectStudentsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getStudentProjectPortfolioFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentProjectPortfolioQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getStudentDetailsDetailsForInsightFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCASInsightQuery,
      variables,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getCASPlannerElementEvidencesFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCASPlannerElementEvidencesQuery,
      variables,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getLearningOutcomesWithEvidencesFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getLearningOutcomesWithEvidencesQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const writeLearningOutcomesWithEvidencesInCache = ({
  id,
  data,
  plannerElementEvidenceFilters,
  isServiceAsAction,
}) => {
  let queryData = {};
  try {
    queryData = client.writeQuery({
      query: getLearningOutcomesWithEvidencesQuery,
      variables: {
        id,
        plannerElementEvidenceFilters,
        isServiceAsAction,
      },
      data,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const writeStudentProjectPortfolioProgressFragmentInCache = ({
  id,
  data,
  variables = {},
}) => {
  try {
    client.writeFragment({
      id: `StudentProjectPortfolio:${id}`,
      fragment: studentPortfolioProgressFragment.studentPortfolioProgress,
      fragmentName: "portfolioProgress",
      data,
      variables,
    });
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getStudentProjectPortfolioProgressFragmentInCache = ({
  id,
  variables = {},
}) => {
  let queryData = {};

  try {
    queryData = client.readFragment({
      id: `StudentProjectPortfolio:${id}`,
      fragment: studentPortfolioProgressFragment.studentPortfolioProgress,
      fragmentName: "portfolioProgress",
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectGroupStudentPortfolioIdFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupStudentPortfolioIdQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getUserProjectGroupsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getUserProjectGroupsQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getProjectGroupYearGroupFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupYearGroupQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getOrganizationStudentsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrganizationStudentsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getUserProjectGroupYearGroupsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getUserProjectGroupYearGroupsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getModuleAdminsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getModuleAdminsQuery,
      variables,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getProjectGroupAssessmentToolDetailsFromCache = ({
  id,
  assessmentToolFilters,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupAssessmentToolDetailsQuery,
      variables: { id, assessmentToolFilters },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getOrganizationAcademicCriteriaValuesFromCache = ({
  organisationId,
  finalGradeFilters,
  fetchFinalGrade,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getOrganizationAcademicCriteriaValuesQuery,
      variables: {
        id: organisationId,
        finalGradeFilters,
        fetchFinalGrade,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getOrganizationAcademicCriteriaValuesMultiDPFromCache = ({
  organisationId,
  curriculumProgramIds,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getOrganizationAcademicCriteriaValuesMultiDPQuery,
      variables: {
        id: organisationId,
        curriculumProgramIds,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const writeProjectRatedCriteriaInCache = ({ id, data }) => {
  try {
    client.writeQuery({
      query: getProjectCriteriaDetailsQuery,
      variables: {
        id,
      },
      data: { node: data },
    });
  } catch (e) {
    console.warn("Unable to write : ", e);
  }
};

export const writeProjectStudentCriteriaDetails = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getProjectStudentCriteriaDetailsQuery,
      variables,
      data: { node: data },
    });
  } catch (e) {
    console.warn("Unable to write : ", e);
  }
};

export const getProjectGroupDetailsFromCache = ({
  id,
  assessmentToolFilters,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupDetailsQuery,
      variables: {
        id,
        assessmentToolFilters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getProjectGroupSetupGuidanceDetailsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupSetupGuidanceDetailsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const writeProjectGroupSetupGuidanceDetailsInCache = ({
  variables,
  data,
}) => {
  try {
    client.writeQuery({
      query: getProjectGroupSetupGuidanceDetailsQuery,
      variables,
      data,
    });
  } catch (e) {
    console.warn("could not write", e);
    return {};
  }
};

export const getProjectCriteriaDetailsFromCache = ({
  id,
  studentCourseFilters = {},
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectCriteriaDetailsQuery,
      variables: {
        id,
        studentCourseFilters,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getProjectStudentCriteriaDetailsFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectStudentCriteriaDetailsQuery,
      variables,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getProjectGroupCourseFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupCourseQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const writeProjectGroupBasicDetailsInCache = ({ id, data }) => {
  try {
    client.writeFragment({
      id: `ProjectGroup:${id}`,
      fragment: projectGroupFragment.projectGroupBasicDetails,
      fragmentName: "projectGroupBasicDetails",
      data,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
};

export const getOrganizationCourseAndOtherStaffFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrganizationCourseAndOtherStaffQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getGroup4ProjectStudentsDetailsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getGroup4ProjectStudentsDetailsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getGroup4ProjectSubjectDetailsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getGroup4ProjectSubjectDetailsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getProjectGroupSubjectGroupIdFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupSubjectGroupIdQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getYearGroupStudentsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getYearGroupStudentsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const readProjectGroupBasicDetailsFromCache = ({ id }) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `ProjectGroup:${id}`,
      fragment: projectGroupFragment.projectGroupBasicDetails,
      fragmentName: "projectGroupBasicDetails",
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getUserProjectGroupPortfoliosCountFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getUserProjectGroupPortfoliosCountQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getStudentPortfolioSupervisorsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentPortfolioSupervisorsQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  return queryData != null ? queryData : {};
};
export const getProjectGroupCourseAcademicYearsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupCourseAcademicYearsQuery,
      variables: params,
    });
  } catch (e) {
    console.error(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getStudentProjectPortfolioFeedbackFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentProjectPortfolioFeedbackQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectGroupProjectAndPortfolioCountFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupProjectAndPortfolioCountQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getPortfolioProjectInfoFragmentFromCache = ({
  id,
  academicYearId,
}) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `Project:${id}`,
      fragment: projectFragments.portfolioProjectInfoFragment,
      fragmentName: "portfolioProjectInfo",
      variables: {
        id,
        academicYearId,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const writePortfolioProjectInfoFragmentInCache = ({
  projectId,
  academicYearId,
  data,
}) => {
  try {
    client.writeFragment({
      id: `Project:${projectId}`,
      fragment: projectFragments.portfolioProjectInfoFragment,
      fragmentName: "portfolioProjectInfo",
      data: data,
      variables: academicYearId,
    });
  } catch (e) {
    console.warn("could not write", e);
    return {};
  }
};

export const getProjectGroupBasicDetailsFromCache = ({ projectGroupId }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupBasicDetailsQuery,
      variables: {
        projectGroupId,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getOrganizationStaffWithExternalUsersFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrganizationStaffWithExternalUsersQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectApprovedSupervisorsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectApprovedSupervisorsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getOrgAdminsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getOrgAdminsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData != null ? queryData : {};
};

export const getProjectAttachmentFragmentFromCache = ({ attachmentId }) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `Attachment:${attachmentId}`,
      fragment: projectAttachmentFragment.attachment,
      fragmentName: "attachmentItemV2",
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const writeProjectAttachmentInCache = ({ attachmentId, data }) => {
  try {
    client.writeFragment({
      id: `Attachment:${attachmentId}`,
      fragment: projectAttachmentFragment.attachment,
      fragmentName: "attachmentItemV2",
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getDPSubjectGroupFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getDPSubjectGroupsQuery,
      variables,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getPlannerElementNodeFromCache = ({ categoryId }) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `PlannerElementNode:${categoryId}`,
      fragment: plannerElementNodeFragment.basicPlannerElementNodeDetail,
      fragmentName: "plannerElementNodeItem",
      variables: {
        id: categoryId,
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getSubjectDetailsFromCache = ({ subjectId }) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `Subject:${subjectId}`,
      fragment: projectGroupFragment.basicSubjectDetails,
      fragmentName: "subjectItem",
      variables: {
        id: subjectId,
        flatSubjectVariantFilters: {
          archivalState: "ALL",
        },
      },
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getFinalGradeFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getFinalGradeRatingQuery,
      variables,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getLearningOutcomesPostsFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getLearningOutcomesPostsQuery,
      variables,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getStudentProjectPortfolioHeaderDetailsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStudentProjectPortfolioHeaderDetailsQuery,
      variables: params,
    });
  } catch (e) {
    console.warn(e);
    return {};
  }
  return queryData ? queryData : {};
};

export const getProjectGroupProjectsIdsFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupProjectsIdsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getProjectGroupCategoriesFromCache = variables => {
  return (
    client.readQuery({
      query: getProjectGroupCategoriesQuery,
      variables,
    })?.node?.projectGroupCategories ?? []
  );
};

export const getProjectGroupAcademicYearsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getProjectGroupAcademicYearsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};
